/**
 * sign-done-trail.ts — "what happens next", after the driver's signature is in.
 *
 * PURE MODULE: no hooks, no fetch, no clock beyond the dates it is handed. `SignDocument`
 * renders the rows; the sequence they describe is decided here, which is what lets a test
 * read it.
 *
 * ── WHY THE SEQUENCE MOVED OFF THIS DOCUMENT'S ROSTER ─────────────────────────
 * The trail used to be built entirely from `agreement_render`'s own party list: driver,
 * witness, Primelane, in sign order on one document. That was a true picture only while the
 * witness signed the hire purchase agreement. THE WITNESS NOW SIGNS THE WITNESS AGREEMENT —
 * a separate instrument, with its own roster — and a hire purchase agreement raised from the
 * current template seats the driver and Primelane alone.
 *
 * So the middle step is read from ANOTHER document: the live WITNESS document on the same
 * contract, through `get_agreement_documents`, which is guarded by `can_access_contract`
 * and therefore readable by the driver whose contract it is.
 *
 * ── EXCEPT ON A DOCUMENT THAT STILL SEATS A WITNESS ITSELF ────────────────────
 * THE THREE-SEAT DOCUMENTS WERE NOT REWRITTEN, and they are the ones a driver is most likely
 * to reopen: a hire purchase agreement pinned to the old template keeps its own WITNESS seat,
 * signed, forever. Deriving the witness step from the contract's witness agreement there
 * would read a document that does not exist and tell somebody holding an executed,
 * fully-signed agreement that their witness has yet to sign it.
 *
 * So the RULE IS PRECEDENCE, not a version flag: a WITNESS seat on THE DOCUMENT BEING VIEWED
 * is that document's own ceremony and outranks everything else — its state is the step's
 * state, and the witness agreement is not consulted at all. The cross-document lookup applies
 * only where the viewed document seats no witness, which is every document raised from the
 * current template.
 *
 * ── WHY PRIMELANE WAITS ON A DOCUMENT THIS ONE DOES NOT NAME ──────────────────
 * `agreement_sign` refuses a COMPANY_REP signature on the hire purchase agreement, the witness
 * agreement AND BOTH ADVANCES — the insurance premium one and, since D5, the regular one
 * [D-B27] — until the driver has executed the hire purchase agreement and the witness has
 * executed the witness agreement (`AGREEMENT_COUNTERSIGN_BLOCKED`), the witness limb being
 * waived where the hire purchase was signed on paper [D2]. The countersign step is therefore
 * `later` until the witness step is done — not because of sign order on this document, but
 * because of a gate that spans them. The two contract instruments are each other's condition;
 * an advance waits on both.
 *
 * ── THE WITNESS STEP DEGRADES, IT NEVER DISAPPEARS ────────────────────────────
 * The documents list is a second read and can be empty for reasons that say nothing about
 * the witness: the query has not resolved, it failed, the subsystem is absent, or the
 * witness agreement has not been created yet. In every one of those the honest row is the
 * same STATIC one — "your witness signs it next" — because that is what happens next in all
 * of them. Only a witness agreement that is actually SIGNED turns the row into a date, so
 * an unread list can never claim a signature that has not been made.
 */

import { format } from 'date-fns';

/** One signature seat, as both `agreement_render` and `get_agreement_documents` project it. */
export interface TrailSeat {
    party: string;
    status: string | null;
    signed_at: string | null;
    /** Optional: only the on-document witness row names its holder in the trail. */
    signer_name?: string | null;
}

/** One `get_agreement_documents` row, as this module reads it. */
export interface TrailDocument {
    doc_type: string;
    status: string;
    signatures: readonly TrailSeat[] | null;
}

export interface TrailStep {
    /** Stable React key, and the identity a test asserts on. */
    id: 'signed' | 'witness' | 'countersign' | 'final';
    state: 'done' | 'now' | 'later';
    title: string;
    detail: string;
}

export interface DoneTrail {
    /** The sentence under the heading: what is outstanding, named. */
    next: string;
    steps: TrailStep[];
}

export interface DoneTrailInput {
    /** `agreement_documents.doc_type` of the document that was just signed. */
    docType: string;
    /** Its status from the render — `COMPLETED` is every seat on it settled. */
    documentStatus: string | null;
    /** When this driver's own signature was taken. */
    mySignedAt: string | null;
    /** This document's own roster: the countersignature seat, and any witness seat on it. */
    parties: readonly TrailSeat[] | null;
    finalizedAt: string | null;
    /**
     * `get_agreement_documents` for this contract. Null/empty degrades, never lies — and it
     * is not read at all where `parties` already seats a witness.
     */
    contractDocuments: readonly TrailDocument[] | null | undefined;
}

/**
 * The instrument whose countersignature waits on the CONTRACT'S witness agreement AND which
 * a driver can be sitting on when this trail is drawn.
 *
 * ── IT IS NOT THE SET THE SERVER GATES, AND IT IS NOT MEANT TO BE ─────────────
 * `agreement_sign` refuses a COMPANY_REP signature on FOUR doc types — the hire purchase
 * agreement, the witness agreement, the insurance premium advance and, since D5, the regular
 * advance [D-B27]. This predicate answers a narrower question: which of them should draw a
 * WITNESS ROW in a trail the DRIVER is looking at, immediately after signing.
 *
 * On the two advances the answer is no, and the trail is not the thing that decides it. Their
 * countersignature waits on the server's gate — the HP signature and the witness record it
 * reads for itself, with the paper-era waiver that skips the witness limb where the hire
 * purchase was signed on paper and no in-app HP document exists [D2]. None of that is visible
 * from the driver's side of an advance, it is not a step the driver can move, and a witness row
 * on an advance would name a ceremony that instrument never had. What the driver is told there
 * is the honest thing: Primelane countersigns next.
 *
 * Of the two that do draw the row, the driver only ever signs the first — the witness signs the
 * other — so naming the type is what keeps a witness row off the documents it would be a
 * fiction on. No behaviour here moves with D5.
 *
 * It answers for the CROSS-DOCUMENT model only. A document carrying its own WITNESS seat is
 * evidence about itself and needs no type test — see the precedence rule in the header.
 */
function witnessGates(docType: string): boolean {
    return docType === 'HIRE_PURCHASE';
}

/**
 * The statuses that RELEASE a document, as a denylist — the same pair the partial unique
 * index behind every arc is keyed on. A voided or superseded witness agreement binds
 * nobody and is not evidence about the witness; anything else, including a status this app
 * has not met yet, is the live one.
 */
function isLive(document: { status: string }): boolean {
    return document.status !== 'VOIDED' && document.status !== 'SUPERSEDED';
}

/** An unparseable or absent timestamp is no date at all, rather than "Invalid Date". */
function day(iso: string | null | undefined): string | null {
    if (!iso) return null;
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? null : format(date, 'MMM d, yyyy');
}

/** The WITNESS seat on the contract's live witness agreement, where one is readable. */
function witnessSeatOnContract(
    documents: readonly TrailDocument[] | null | undefined,
): TrailSeat | null {
    for (const document of documents ?? []) {
        if (document.doc_type !== 'WITNESS' || !isLive(document)) continue;
        const seat = (document.signatures ?? []).find((row) => row.party === 'WITNESS');
        if (seat) return seat;
    }
    return null;
}

export function signDoneTrail(input: DoneTrailInput): DoneTrail {
    const executed = input.documentStatus === 'COMPLETED';
    const parties = input.parties ?? [];
    const company = parties.find((party) => party.party === 'COMPANY_REP') ?? null;

    // PRECEDENCE. A WITNESS seat on the document in front of the driver is this document's
    // own ceremony: it settles the step by itself, and the contract's witness agreement —
    // which on such a contract does not exist — is never read.
    const seatedWitness = parties.find((party) => party.party === 'WITNESS') ?? null;
    const witnessSeat =
        seatedWitness ??
        (witnessGates(input.docType) ? witnessSeatOnContract(input.contractDocuments) : null);
    const waitsOnTheWitness = seatedWitness !== null || witnessGates(input.docType);
    const witnessSigned = witnessSeat?.status === 'SIGNED';
    const witnessDay = witnessSigned ? day(witnessSeat?.signed_at) : null;

    const steps: TrailStep[] = [
        {
            id: 'signed',
            state: 'done',
            title: 'You signed',
            detail: day(input.mySignedAt) ?? 'Recorded',
        },
    ];

    if (waitsOnTheWitness) {
        steps.push({
            id: 'witness',
            state: witnessSigned ? 'done' : 'now',
            // A seat on THIS document keeps the wording that document's own roster earned —
            // the witness signs the thing the driver just signed, and it names them. Only the
            // cross-document model may name a second instrument.
            title: seatedWitness
                ? `${(seatedWitness.signer_name ?? '').trim() || 'Your witness'} witnesses`
                : 'Your witness signs the witness agreement',
            // A signature whose date did not survive the read is still a signature: the row
            // says so without inventing a day for it.
            detail: witnessSigned
                ? (witnessDay ?? 'Signed')
                : seatedWitness
                  ? 'Primelane shares a signing link with them'
                  : 'Primelane shares a signing link with them for their own agreement',
        });
    }

    if (company) {
        const countersigned = company.status === 'SIGNED';
        steps.push({
            id: 'countersign',
            state: countersigned
                ? 'done'
                : waitsOnTheWitness && !witnessSigned
                  ? 'later'
                  : 'now',
            title: 'Primelane countersigns',
            // "Both agreements" is the cross-document gate speaking. Where the witness sits on
            // this very document there is only one agreement, and the countersignature is
            // literally the last signature on it.
            detail: countersigned
                ? (day(company.signed_at) ?? 'Signed')
                : waitsOnTheWitness && !seatedWitness
                  ? 'Once both agreements carry a signature'
                  : 'The last signature on the document',
        });
    }

    steps.push({
        id: 'final',
        state: input.finalizedAt ? 'done' : 'later',
        title: 'Final PDF issued',
        // "Ready to view", NOT "Ready to download". The executed instrument is read in the
        // app and never handed over (owner ruling, 2026-08-21): there is no download on this
        // step, on the documents card, or on the public link's done step. A trail row that
        // promised one would be the last place in the app still offering it.
        detail: input.finalizedAt
            ? 'Ready to view'
            : 'Usually within a few minutes of the last signature',
    });

    return {
        next: nextSentence({
            executed,
            waitsOnTheWitness,
            onThisDocument: seatedWitness !== null,
            witnessSigned,
        }),
        steps,
    };
}

/**
 * THE SENTENCE NAMES WHO IS OUTSTANDING, because "the remaining parties sign next" was the
 * plural that hid which party, and on a hire purchase agreement raised from the current
 * template it also hid the fact that the wait is on a DIFFERENT document being signed.
 *
 * Which it must not say on a document that seats the witness itself: there the wait is on
 * this document, and naming a second one would send a driver looking for it.
 */
function nextSentence(input: {
    executed: boolean;
    waitsOnTheWitness: boolean;
    onThisDocument: boolean;
    witnessSigned: boolean;
}): string {
    if (input.executed) return 'Every party has signed. Your final copy is below.';
    if (input.waitsOnTheWitness && !input.witnessSigned) {
        return input.onThisDocument
            ? 'Your witness signs next, then Primelane countersigns. ' +
                  'Your final copy appears here once they have.'
            : 'Your witness signs the witness agreement next, then Primelane countersigns. ' +
                  'Your final copy appears here once they have.';
    }
    return 'Primelane countersigns next. Your final copy appears here once they have.';
}
